import {
	AttachmentList,
	BusyIndicator,
	ContainerWithBusyIndicator,
	Icon,
	Portal
} from "client/Components";
import { generateTrackingId } from "client/Misc/utils";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	AttachmentItem,
	AttachmentStatus,
	UploadedInlineAttachment,
	UploadedNormalAttachment
} from "../../AttachmentManager/common";
import { useViewAttachmentHandler } from "../../../EmailViewer/Attachements/Actions/useViewAttachementHandler";
import { MailMessageLocationModel } from "client/Api/Email/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { useIsTouchDevice } from "client/Hooks";

export type AttachmentsProps = {
	items: readonly AttachmentItem[];
	draftLocation: MailMessageLocationModel;
	onDelete(uid: string): void;
};

export const Attachments = (props: AttachmentsProps): ReactElement => {
	const {
		items,
		draftLocation,
		onDelete
	} = props;

	const isTouch: boolean = useIsTouchDevice();

	return (
		<AttachmentList
			trackingId="attachments"
		>
			{items.map(
				a => (
					<Item
						key={a.uid}
						item={a}
						draftLocation={draftLocation}
						isTouch={isTouch}
						onDelete={onDelete}
					/>
				)
			)}
		</AttachmentList>
	);
};
Attachments.displayName = "Attachments";

type ItemProps = {
	item: AttachmentItem;
	draftLocation: MailMessageLocationModel;
	isTouch: boolean;
	onDelete(uid: string): void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		draftLocation,
		isTouch,
		onDelete
	} = props;

	const {
		name
	} = item;

	const trackingId: string = generateTrackingId("attachment", `|${item.uid}`);

	const renderLoadingThumbnail = (): ReactElement => {
		return (
			<ContainerWithBusyIndicator
				size="fill"
				hasBusyIndicator
			/>
		);
	};

	return (
		<>
			{item.status === AttachmentStatus.Uploading
				? (
					<AttachmentList.Item
						trackingId={trackingId}
						isTouch={isTouch}
					>
						<AttachmentList.Item.Content
							name={name}
							thumbnail={renderLoadingThumbnail()}
						/>
					</AttachmentList.Item>
				) : (
					<LoadedItem
						item={item}
						draftLocation={draftLocation}
						onDelete={onDelete}
						trackingId={trackingId}
					/>
				)
			}
		</>
	);
};
Item.displayName = "Item";

type LoadedItemProps = {
	item: UploadedNormalAttachment | UploadedInlineAttachment;
	draftLocation: MailMessageLocationModel;
	onDelete(uid: string): void;
	trackingId: string;
};

const LoadedItem = (props: LoadedItemProps): ReactElement => {
	const {
		item,
		draftLocation,
		onDelete,
		trackingId
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const isTouch: boolean = useIsTouchDevice();

	const {
		name
	} = item;

	const {
		type
	} = getExtensionAndType(name);

	const isSharepointConfigured: boolean = useMemo(
		(): boolean => {
			return documentsConfiguration().sharePoint
				.isConfigured;
		},
		[]
	);

	const viewAttachmentHandler: (() => void) = useViewAttachmentHandler(
		{
			attachment: {
				exchangeAttachmentId: item.exchangeId,
				name: item.name
			},
			messageLocation: draftLocation,
			onIsBusyChange: setIsBusy
		}
	);

	const actions = useMemo(
		(): Action<AttachmentItem>[] => {
			return [
				{
					kind: ActionKind.Regular,
					name: "view",
					title: localize("_View"),
					icon: "eye",
					canExecute: () => isSharepointConfigured,
					execute: (): void => viewAttachmentHandler()
				},
				{
					kind: ActionKind.Regular,
					name: "delete",
					title: localize("_Delete"),
					icon: "trash-can",
					canExecute(): boolean {
						return true;
					},
					execute(item: AttachmentItem): void {
						onDelete(item.uid);
					}
				}
			];
		},
		[
			isSharepointConfigured,
			onDelete,
			viewAttachmentHandler
		]
	);

	return (
		<>
			<AttachmentList.Item
				actions={actions}
				actionContext={item}
				actionsTitle={name}
				trackingId={trackingId}
				isTouch={isTouch}
				onClick={isSharepointConfigured
					? viewAttachmentHandler
					: undefined
				}
			>
				<AttachmentList.Item.Content
					name={name}
					thumbnail={
						<Icon
							value={{
								kind: "svg",
								name: type
							}}
						/>
					}
					actions={actions}
					actionContext={item}
				/>
			</AttachmentList.Item>
			{isBusy && (
				<Portal
					container={document.body}
				>
					<BusyIndicator />
				</Portal>
			)}
		</>
	);
};